import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import api from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';

interface Game {
  id: string;
  opponentName: string;
  leagueName: string | null;
  legs: number;
  scheduledStart: string;
  scheduledEnd: string;
  status: string;
}

interface PendingGame {
  id: string;
  opponentName: string;
  requesterName: string;
  requesterProfileImage: string | null;
  leagueName: string | null;
  legs: number;
  scheduledStart: string;
  scheduledEnd: string;
  notes: string | null;
  createdAt: string;
}

interface MyRequest {
  id: string;
  opponentName: string;
  ownerProfileImage: string | null;
  leagueName: string | null;
  legs: number;
  scheduledStart: string;
  scheduledEnd: string;
  status: string;
  declineReason: string | null;
  createdAt: string;
}

interface Stats {
  upcomingGames: number;
  completedGames: number;
  activeLinks: number;
  pendingRequests: number;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [upcomingGames, setUpcomingGames] = useState<Game[]>([]);
  const [pendingGames, setPendingGames] = useState<PendingGame[]>([]);
  const [myRequests, setMyRequests] = useState<MyRequest[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      // Fetch upcoming games (scheduled status means not yet completed)
      const gamesResponse = await api.get('/games', {
        params: {
          status: 'scheduled',
          limit: 5
        }
      });

      // Sort by date ascending (soonest first)
      const sortedGames = gamesResponse.data.games.sort(
        (a: Game, b: Game) => new Date(a.scheduledStart).getTime() - new Date(b.scheduledStart).getTime()
      );

      setUpcomingGames(sortedGames);

      // Fetch pending game requests and my requests
      const [pendingRes, myRequestsRes] = await Promise.all([
        api.get('/games/pending'),
        api.get('/games/my-requests')
      ]);

      setPendingGames(pendingRes.data.pendingGames || []);
      setMyRequests(myRequestsRes.data.myRequests || []);

      // Fetch stats
      const [scheduledRes, completedRes, linksRes] = await Promise.all([
        api.get('/games', { params: { status: 'scheduled' } }),
        api.get('/games', { params: { status: 'completed' } }),
        api.get('/booking-links')
      ]);

      setStats({
        upcomingGames: scheduledRes.data.total,
        completedGames: completedRes.data.total,
        activeLinks: linksRes.data.activeCount,
        pendingRequests: pendingRes.data.pendingGames?.length || 0
      });
    } catch {
      setError('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAcceptGame = async (gameId: string) => {
    setActionLoading(gameId);
    try {
      await api.post(`/games/${gameId}/accept`);
      // Refresh data
      await fetchData();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to accept game');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeclineGame = async (gameId: string) => {
    setActionLoading(gameId);
    try {
      await api.post(`/games/${gameId}/decline`);
      // Refresh data
      await fetchData();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to decline game');
    } finally {
      setActionLoading(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/50 text-red-300 px-4 py-3 rounded-lg border border-red-800">
        {error}
        <button
          onClick={() => { setError(''); fetchData(); }}
          className="ml-4 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="page-title">Dashboard</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="text-sm text-gray-400">Upcoming Games</div>
          <div className="text-3xl font-bold text-[#1111d4]">
            {stats?.upcomingGames || 0}
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-400">Completed Games</div>
          <div className="text-3xl font-bold text-green-400">
            {stats?.completedGames || 0}
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-400">Active Links</div>
          <div className="text-3xl font-bold text-purple-400">
            {stats?.activeLinks || 0}
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-400">Pending Requests</div>
          <div className="text-3xl font-bold text-yellow-400">
            {stats?.pendingRequests || 0}
          </div>
        </div>
      </div>

      {/* Pending Game Requests - Show prominently if there are any */}
      {pendingGames.length > 0 && (
        <div className="card border-2 border-yellow-500/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-yellow-400">
              Game Requests ({pendingGames.length})
            </h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Friends want to play! Accept or decline their requests.
          </p>

          <div className="space-y-3">
            {pendingGames.map((game) => (
              <div
                key={game.id}
                className="p-4 bg-[#252550] rounded-lg"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {game.requesterProfileImage ? (
                      <img
                        src={game.requesterProfileImage}
                        alt={game.requesterName}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#1111d4] flex items-center justify-center text-white font-bold">
                        {game.requesterName?.charAt(0).toUpperCase() || '?'}
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-white">
                        {game.requesterName}
                      </div>
                      <div className="text-sm text-gray-400">
                        {game.leagueName || 'Casual'} - {game.legs} legs
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-white">
                      {format(parseISO(game.scheduledStart), 'EEE, MMM d')}
                    </div>
                    <div className="text-sm text-gray-400">
                      {format(parseISO(game.scheduledStart), 'h:mm a')}
                    </div>
                  </div>
                </div>

                {game.notes && (
                  <p className="text-sm text-gray-400 mb-3 italic">
                    "{game.notes}"
                  </p>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => handleAcceptGame(game.id)}
                    disabled={actionLoading === game.id}
                    className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    {actionLoading === game.id ? 'Processing...' : 'Accept'}
                  </button>
                  <button
                    onClick={() => handleDeclineGame(game.id)}
                    disabled={actionLoading === game.id}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Pending Requests (sent by me) */}
      {myRequests.filter(r => r.status === 'pending_approval').length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">
            Your Pending Requests
          </h2>
          <p className="text-sm text-gray-400 mb-4">
            Waiting for your friends to respond
          </p>

          <div className="space-y-3">
            {myRequests
              .filter(r => r.status === 'pending_approval')
              .map((request) => (
                <div
                  key={request.id}
                  className="flex items-center justify-between p-3 bg-[#252550] rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {request.ownerProfileImage ? (
                      <img
                        src={request.ownerProfileImage}
                        alt={request.opponentName}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center text-white text-sm font-bold">
                        {request.opponentName?.charAt(0).toUpperCase() || '?'}
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-white">
                        vs {request.opponentName}
                      </div>
                      <div className="text-sm text-gray-400">
                        {request.leagueName || 'Casual'} - {request.legs} legs
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-white">
                      {format(parseISO(request.scheduledStart), 'EEE, MMM d')}
                    </div>
                    <div className="text-xs text-yellow-400">
                      Awaiting response
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Declined Requests (show briefly) */}
      {myRequests.filter(r => r.status === 'declined').length > 0 && (
        <div className="card border border-red-500/30">
          <h2 className="text-lg font-semibold text-red-400 mb-4">
            Declined Requests
          </h2>

          <div className="space-y-3">
            {myRequests
              .filter(r => r.status === 'declined')
              .slice(0, 3)
              .map((request) => (
                <div
                  key={request.id}
                  className="flex items-center justify-between p-3 bg-[#252550] rounded-lg opacity-75"
                >
                  <div>
                    <div className="font-medium text-white">
                      vs {request.opponentName}
                    </div>
                    <div className="text-sm text-gray-400">
                      {format(parseISO(request.scheduledStart), 'EEE, MMM d')} at {format(parseISO(request.scheduledStart), 'h:mm a')}
                    </div>
                    {request.declineReason && (
                      <div className="text-xs text-red-400 mt-1">
                        Reason: {request.declineReason}
                      </div>
                    )}
                  </div>
                  <div className="text-red-400 text-sm">
                    Declined
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Upcoming Games */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Upcoming Games</h2>
          <Link to="/games" className="text-sm text-[#1111d4] hover:text-[#1414f0] hover:underline">
            View all
          </Link>
        </div>

        {upcomingGames.length === 0 ? (
          <p className="text-gray-400 text-center py-8">
            No upcoming games scheduled.{' '}
            <Link to="/friends" className="text-[#1111d4] hover:underline">
              Book a game with a friend
            </Link>{' '}
            or{' '}
            <Link to="/links" className="text-[#1111d4] hover:underline">
              create a booking link
            </Link>{' '}
            to get started.
          </p>
        ) : (
          <div className="space-y-3">
            {upcomingGames.map((game) => (
              <div
                key={game.id}
                className="flex items-center justify-between p-3 bg-[#252550] rounded-lg"
              >
                <div>
                  <div className="font-medium text-white">
                    {user?.displayName || 'You'}
                    <span className="text-gray-400 mx-2">vs</span>
                    {game.opponentName}
                  </div>
                  <div className="text-sm text-gray-400">
                    {game.leagueName || 'Casual'} - {game.legs} legs
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-white">
                    {format(parseISO(game.scheduledStart), 'EEE, MMM d')}
                  </div>
                  <div className="text-sm text-gray-400">
                    {format(parseISO(game.scheduledStart), 'h:mm a')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/friends" className="btn-primary">
            Book with Friend
          </Link>
          <Link to="/links" className="btn-secondary">
            Create Booking Link
          </Link>
          <Link to="/availability" className="btn-secondary">
            Set Availability
          </Link>
        </div>
      </div>
    </div>
  );
}
